import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, NgZone, PLATFORM_ID, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Company } from '@core/models/company';
import { User } from '@core/models/user';

import { CompanyService } from '@core/services/company.service';
import { UserService } from '@core/services/user.service';
import { clearPendingPartnerCode, readPendingPartnerCode } from '@core/utils/pending-partner-code';
import { AuthService } from '@core/services/auth.service';
import { ThemeService } from '@core/services/theme.service';
import { ProfileValidationService } from '@core/services/profile-validation.service';
import { ApiConfigurationService } from '@core/services/api-configuration.service';
import { CompanyMembershipService } from '@core/services/company-membership.service';

interface GoogleAddressComponent {
  longText?: string;
  shortText?: string;
  types: string[];
}

interface GooglePlace {
  addressComponents?: GoogleAddressComponent[];
  fetchFields(options: { fields: string[] }): Promise<void>;
}

interface GooglePlacePrediction {
  text: string | { toString(): string };
  toPlace(): GooglePlace;
}

interface GoogleAutocompleteSuggestion {
  placePrediction?: GooglePlacePrediction;
}

interface GooglePlacesLibrary {
  AutocompleteSessionToken: new () => object;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(request: {
      input: string;
      includedRegionCodes: string[];
      includedPrimaryTypes: string[];
      sessionToken: object;
    }): Promise<{ suggestions: GoogleAutocompleteSuggestion[] }>;
  };
}

interface GoogleMapsWindow extends Window {
  google?: {
    maps?: {
      importLibrary?: (libraryName: 'places') => Promise<GooglePlacesLibrary>;
    };
  };
  gm_authFailure?: () => void;
}

interface AddressSuggestion {
  id: string;
  prediction: GooglePlacePrediction;
  text: string;
}

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrl: './company.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyComponent implements OnInit, AfterViewInit, OnDestroy {
  private static googleMapsScriptPromise: Promise<void> | null = null;

  private fb = inject(FormBuilder);
  private companyService = inject(CompanyService);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private themeService = inject(ThemeService);
  private profileValidationService = inject(ProfileValidationService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private apiConfigService = inject(ApiConfigurationService);
  private membershipService = inject(CompanyMembershipService);

  @ViewChild('streetInput') streetInput!: ElementRef;
  suggestions: AddressSuggestion[] = [];
  activeSuggestionIndex = -1;

  companyForm!: FormGroup;
  user!: User;
  uploadPercent?: Observable<number>;
  downloadURL?: string;
  loading = false;
  serverMessage?: string;

  private readonly suggestionInput = new Subject<string>();
  private suggestionSubscription?: Subscription;
  private inputListener?: (event: Event) => void;
  private placesLibrary?: GooglePlacesLibrary;
  private sessionToken?: object;
  private suggestionRequestId = 0;
  private priorAuthFailure?: () => void;
  private authFailureHandler?: () => void;
  private authFailureLogged = false;
  /** Set in ngOnDestroy so async Maps setup that settles late does nothing. */
  private destroyed = false;

  // Tier information — all new registrations are professional trial
  userTier: 'free' | 'trial' | 'premium' = 'premium';
  isTrialOrPremium = true;

  // US states for billing address select
  readonly usStates: { name: string; abbr: string }[] = [
    { name: 'Alabama', abbr: 'AL' }, { name: 'Alaska', abbr: 'AK' },
    { name: 'Arizona', abbr: 'AZ' }, { name: 'Arkansas', abbr: 'AR' },
    { name: 'California', abbr: 'CA' }, { name: 'Colorado', abbr: 'CO' },
    { name: 'Connecticut', abbr: 'CT' }, { name: 'Delaware', abbr: 'DE' },
    { name: 'District of Columbia', abbr: 'DC' }, { name: 'Florida', abbr: 'FL' },
    { name: 'Georgia', abbr: 'GA' }, { name: 'Hawaii', abbr: 'HI' },
    { name: 'Idaho', abbr: 'ID' }, { name: 'Illinois', abbr: 'IL' },
    { name: 'Indiana', abbr: 'IN' }, { name: 'Iowa', abbr: 'IA' },
    { name: 'Kansas', abbr: 'KS' }, { name: 'Kentucky', abbr: 'KY' },
    { name: 'Louisiana', abbr: 'LA' }, { name: 'Maine', abbr: 'ME' },
    { name: 'Maryland', abbr: 'MD' }, { name: 'Massachusetts', abbr: 'MA' },
    { name: 'Michigan', abbr: 'MI' }, { name: 'Minnesota', abbr: 'MN' },
    { name: 'Mississippi', abbr: 'MS' }, { name: 'Missouri', abbr: 'MO' },
    { name: 'Montana', abbr: 'MT' }, { name: 'Nebraska', abbr: 'NE' },
    { name: 'Nevada', abbr: 'NV' }, { name: 'New Hampshire', abbr: 'NH' },
    { name: 'New Jersey', abbr: 'NJ' }, { name: 'New Mexico', abbr: 'NM' },
    { name: 'New York', abbr: 'NY' }, { name: 'North Carolina', abbr: 'NC' },
    { name: 'North Dakota', abbr: 'ND' }, { name: 'Ohio', abbr: 'OH' },
    { name: 'Oklahoma', abbr: 'OK' }, { name: 'Oregon', abbr: 'OR' },
    { name: 'Pennsylvania', abbr: 'PA' }, { name: 'Rhode Island', abbr: 'RI' },
    { name: 'South Carolina', abbr: 'SC' }, { name: 'South Dakota', abbr: 'SD' },
    { name: 'Tennessee', abbr: 'TN' }, { name: 'Texas', abbr: 'TX' },
    { name: 'Utah', abbr: 'UT' }, { name: 'Vermont', abbr: 'VT' },
    { name: 'Virginia', abbr: 'VA' }, { name: 'Washington', abbr: 'WA' },
    { name: 'West Virginia', abbr: 'WV' }, { name: 'Wisconsin', abbr: 'WI' },
    { name: 'Wyoming', abbr: 'WY' },
  ];

  // Dropdown options
  industryOptions = [
    'Technology',
    'Healthcare',
    'Financial Services',
    'Manufacturing',
    'Retail',
    'Education',
    'Government',
    'Non-Profit',
    'Energy',
    'Transportation',
    'Other',
  ];

  companySizeOptions = ['1-10', '11-50', '51-200', '201-500', '500+'];

  businessTypeOptions = [
    'Corporation',
    'LLC',
    'Partnership',
    'Sole Proprietorship',
    'Non-Profit',
    'Government Agency',
    'Other',
  ];

  complianceOptions = [
    'PCI DSS',
    'HIPAA',
    'SOX',
    'GDPR',
    'ISO 27001',
    'NIST',
    'Other',
  ];

  companyUploadDirectory = 'companylogos/' + this.randomString(10);

  constructor() {
    this.themeService.setSelectPlanPageLayout(true);
    this.themeService.showSpinner();
    this.userService.getUserModel().subscribe((user: User | null) => {
      if (user) {
        this.user = user;
        this.initializeTierInfo();
        this.prefillFromSignup();
        this.themeService.hideSpinner();
      }
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    // Form is ready — apply trial validators immediately
    this.setupFormValidation();
    this.prefillFromSignup();
  }

  /**
   * Prefills the company name typed at quick signup (held on the user document
   * until the email was verified), so it is never asked for twice (#870).
   */
  private prefillFromSignup(): void {
    const nameControl = this.companyForm?.get('name');
    const pendingName = this.user?.pendingCompanyName;
    if (nameControl && pendingName && !nameControl.value) {
      nameControl.setValue(pendingName);
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.apiConfigService.getGoogleMapsApiKey().then((key) => {
        if (key && !this.destroyed) {
          this.installAuthFailureHandler();
          this.loadGooglePlacesScript(key)
            .then(() => this.initAutocomplete())
            .catch(() => this.disableAutocomplete());
        }
      });
    }
  }

  /**
   * Load the Maps JavaScript API once. Places is imported separately on demand.
   */
  private loadGooglePlacesScript(apiKey: string): Promise<void> {
    if ((window as GoogleMapsWindow).google?.maps?.importLibrary) {
      return Promise.resolve();
    }
    if (CompanyComponent.googleMapsScriptPromise) {
      return CompanyComponent.googleMapsScriptPromise;
    }

    const scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.getElementById('gm-places-script') as HTMLScriptElement | null;
      const mapsScript = script ?? document.createElement('script');
      mapsScript.id = 'gm-places-script';
      mapsScript.async = true;
      mapsScript.src = `https://maps.googleapis.com/maps/api/js?${new URLSearchParams({
        key: apiKey,
        loading: 'async',
        v: 'weekly',
      }).toString()}`;
      mapsScript.onload = () => {
        if ((window as GoogleMapsWindow).google?.maps?.importLibrary) {
          resolve();
        } else {
          reject(new Error('Google Maps importLibrary is unavailable'));
        }
      };
      mapsScript.onerror = () => reject(new Error('Google Maps script failed to load'));
      if (!script) {
        document.head.appendChild(mapsScript);
      }
    }).catch((error: unknown) => {
      CompanyComponent.googleMapsScriptPromise = null;
      throw error;
    });
    CompanyComponent.googleMapsScriptPromise = scriptPromise;

    return scriptPromise;
  }

  /**
   * Import Places and listen for address queries using the Places API (New).
   */
  private async initAutocomplete(): Promise<void> {
    if (this.destroyed || this.authFailureLogged || !this.streetInput?.nativeElement) return;

    const maps = (window as GoogleMapsWindow).google?.maps;
    if (!maps?.importLibrary) return;

    try {
      const placesLibrary = await maps.importLibrary('places');
      if (this.destroyed || this.authFailureLogged) return;
      this.placesLibrary = placesLibrary;
      const input = this.streetInput.nativeElement as HTMLInputElement;
      this.inputListener = (event: Event) => {
        const value = (event.target as HTMLInputElement).value;
        this.suggestionRequestId++;
        if (!value.trim()) {
          this.suggestions = [];
          this.activeSuggestionIndex = -1;
          return;
        }
        this.suggestionInput.next(value);
      };
      this.suggestionSubscription = this.suggestionInput
        .pipe(debounceTime(250))
        .subscribe((value) => void this.fetchSuggestions(value));
      input.addEventListener('input', this.inputListener);
    } catch {
      this.disableAutocomplete();
    }
  }

  /**
   * Fetch address predictions without preventing manual address entry on failure.
   */
  private async fetchSuggestions(input: string): Promise<void> {
    const places = this.placesLibrary;
    if (!places || !input.trim()) return;

    const requestId = this.suggestionRequestId;
    this.sessionToken ??= new places.AutocompleteSessionToken();
    try {
      const response = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ['us'],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
        sessionToken: this.sessionToken,
      });
      if (requestId !== this.suggestionRequestId) return;
      this.suggestions = response.suggestions.flatMap((suggestion, index) => {
        const prediction = suggestion.placePrediction;
        return prediction
          ? [{ id: `address-suggestion-${index}`, prediction, text: String(prediction.text) }]
          : [];
      });
      this.activeSuggestionIndex = -1;
    } catch {
      this.disableAutocomplete();
    }
  }

  /**
   * Move through predictions with the keyboard or select the active result.
   */
  onSuggestionKeydown(event: KeyboardEvent): void {
    if (!this.suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeSuggestionIndex = (this.activeSuggestionIndex + 1) % this.suggestions.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeSuggestionIndex =
        this.activeSuggestionIndex <= 0
          ? this.suggestions.length - 1
          : this.activeSuggestionIndex - 1;
    } else if (event.key === 'Enter' && this.activeSuggestionIndex >= 0) {
      event.preventDefault();
      void this.selectSuggestion(this.activeSuggestionIndex);
    } else if (event.key === 'Escape') {
      this.closeSuggestions();
    }
  }

  /**
   * Hide the suggestion list, e.g. when focus leaves the street field. Option
   * clicks keep focus on the input (mousedown is prevented), so they still land.
   */
  closeSuggestions(): void {
    this.suggestions = [];
    this.activeSuggestionIndex = -1;
  }

  /**
   * Retrieve address components for the chosen prediction and update the form.
   */
  async selectSuggestion(index: number): Promise<void> {
    const selected = this.suggestions[index];
    if (!selected) return;

    const token = this.sessionToken;
    const place = selected.prediction.toPlace();
    this.suggestions = [];
    this.activeSuggestionIndex = -1;
    const requestId = ++this.suggestionRequestId;
    try {
      await place.fetchFields({ fields: ['addressComponents'] });
      if (requestId !== this.suggestionRequestId) return;
      const components = place.addressComponents ?? [];
      const componentFor = (type: string) =>
        components.find((component) => component.types.includes(type));
      const city =
        componentFor('locality') ??
        componentFor('sublocality_level_1') ??
        componentFor('postal_town');

      this.ngZone.run(() => {
        const billing = this.companyForm.get('billingAddress');
        const mappedValues = {
          street: `${componentFor('street_number')?.longText ?? ''} ${
            componentFor('route')?.shortText ?? ''
          }`.trim(),
          city: city?.longText ?? '',
          state: componentFor('administrative_area_level_1')?.shortText ?? '',
          postalCode: componentFor('postal_code')?.longText ?? '',
        };
        for (const [field, value] of Object.entries(mappedValues)) {
          billing?.get(field)?.setValue(value);
          billing?.get(field)?.markAsDirty();
        }
      });
    } catch {
      this.disableAutocomplete();
    } finally {
      if (this.sessionToken === token) {
        this.sessionToken = undefined;
      }
    }
  }

  /**
   * Close suggestions when the Maps API is unavailable and leave the form editable.
   */
  private disableAutocomplete(): void {
    // Invalidate any suggestions/details request still awaiting Google, so its
    // completion cannot repopulate the list or write to the form afterwards.
    this.suggestionRequestId++;
    this.suggestionSubscription?.unsubscribe();
    this.suggestionSubscription = undefined;
    if (this.inputListener && this.streetInput?.nativeElement) {
      this.streetInput.nativeElement.removeEventListener('input', this.inputListener);
    }
    this.inputListener = undefined;
    this.placesLibrary = undefined;
    this.sessionToken = undefined;
    this.suggestions = [];
    this.activeSuggestionIndex = -1;
    if (this.streetInput?.nativeElement) {
      (this.streetInput.nativeElement as HTMLInputElement).disabled = false;
    }
  }

  private installAuthFailureHandler(): void {
    const mapsWindow = window as GoogleMapsWindow;
    this.priorAuthFailure = mapsWindow.gm_authFailure;
    this.authFailureHandler = () => {
      this.disableAutocomplete();
      if (!this.authFailureLogged) {
        console.warn('Google Maps authentication failed; address autocomplete is unavailable.');
        this.authFailureLogged = true;
      }
    };
    mapsWindow.gm_authFailure = this.authFailureHandler;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.disableAutocomplete();
    this.suggestionInput.complete();
    // ngOnDestroy also runs during SSR, where there is no window.
    if (!isPlatformBrowser(this.platformId)) return;
    const mapsWindow = window as GoogleMapsWindow;
    if (mapsWindow.gm_authFailure === this.authFailureHandler) {
      mapsWindow.gm_authFailure = this.priorAuthFailure;
    }
  }

  /**
   * Initialize tier information and determine requirements.
   * Every new registration is a paid account (#847), so we always render the
   * full business-info form section.
   */
  private initializeTierInfo(): void {
    this.userTier = 'premium';
    this.isTrialOrPremium = true;
    // Validators may not be applied yet if ngOnInit hasn't run; guard accordingly.
    if (this.companyForm) {
      this.setupFormValidation();
    }
  }

  /**
   * Initialize the company form with tier-specific validators
   */
  private initializeForm(): void {
    this.companyForm = this.fb.group({
      // Basic company information (required for all tiers)
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      image_url: [''],
      website: [''],

      // Tier-specific business information
      industry: [''],
      companySize: [''],
      businessType: [''],
      complianceRequirements: [[]],

      // Billing address — country locked to US; state filled by Places autocomplete
      billingAddress: this.fb.group({
        street: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: ['US'],
      }),
    });
  }

  /**
   * Set up tier-specific form validation
   */
  private setupFormValidation(): void {
    if (!this.companyForm) {
      console.warn(
        'CompanyForm not initialized yet, skipping validation setup',
      );
      return;
    }

    if (this.isTrialOrPremium) {
      // Make business information required for trial/premium tiers
      this.companyForm.get('industry')?.setValidators([Validators.required]);
      this.companyForm.get('companySize')?.setValidators([Validators.required]);
      this.companyForm
        .get('businessType')
        ?.setValidators([Validators.required]);

      // Make billing address required for trial/premium
      const billingAddress = this.companyForm.get('billingAddress');
      billingAddress?.get('street')?.setValidators([Validators.required]);
      billingAddress?.get('city')?.setValidators([Validators.required]);
      billingAddress?.get('state')?.setValidators([Validators.required]);
      billingAddress?.get('postalCode')?.setValidators([Validators.required]);
      billingAddress?.get('country')?.setValidators([Validators.required]);
    } else {
      // Clear validators for free tier
      this.companyForm.get('industry')?.clearValidators();
      this.companyForm.get('companySize')?.clearValidators();
      this.companyForm.get('businessType')?.clearValidators();

      const billingAddress = this.companyForm.get('billingAddress');
      billingAddress?.get('street')?.clearValidators();
      billingAddress?.get('city')?.clearValidators();
      billingAddress?.get('state')?.clearValidators();
      billingAddress?.get('postalCode')?.clearValidators();
      billingAddress?.get('country')?.clearValidators();
    }

    // Update validation status
    this.companyForm.updateValueAndValidity();
  }

  onUploadComplete(url: string) {
    this.downloadURL = url;
  }

  async onSubmit() {
    if (!this.user || !this.companyForm.valid) {
      this.markFormGroupTouched();
      return;
    }

    this.loading = true;
    this.serverMessage = undefined;
    this.themeService.showSpinner();

    try {
      // CRITICAL: Fetch fresh user data from Firestore to ensure we have the correct state
      // This prevents Firestore rule evaluation failures due to stale local data
      const freshUserData = await this.userService.getDocument(this.user.uid);
      if (freshUserData) {
        this.user = new User(freshUserData);
      }

      let companyData = this.companyForm.value;
      companyData = new Company(companyData);
      companyData.ownerId = this.user.uid;
      companyData = this.companyService.updateDomainFromOwnerEmail(
        companyData,
        this.user,
      );

      // Initialize company fields with tier-appropriate defaults
      companyData.createdAt = new Date().toISOString();
      companyData.updatedAt = new Date().toISOString();
      companyData.aiQueriesUsedToday = 0;
      companyData.lastAiQueryReset = new Date().toISOString();
      companyData.featureFlags = {};

      // New companies start with no plan. Self-serve access begins at Stripe
      // checkout; a trial is granted only by sales, from the admin console
      // (#847).
      companyData.tier = 'free';
      companyData.trialStartDate = null;
      companyData.trialEndDate = null;
      companyData.subscriptionStatus = null;

      // Set a accountUuid based on company name for identification
      const timestamp = new Date().getTime();
      const companySlug = companyData.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      companyData.accountUuid = `${companySlug}-${timestamp.toString().slice(-6)}`;

      // Handle logo upload
      if (this.downloadURL) {
        companyData.image_url = this.downloadURL;
      }

      // Set owner information
      if (!companyData.owner) {
        companyData.owner = this.user;
      }
      if (!companyData.ownerId) {
        companyData.ownerId = this.user.uid;
      }

      // Create company - use await instead of .then() for proper error handling
      const res = await this.companyService.create(companyData);

      // create() catches Firestore errors and returns them instead of throwing.
      // Detect that case here so we surface a real error rather than silently
      // writing an undefined companyId to the user record.
      if (!res || res instanceof Error || !res.uid) {
        const reason = res instanceof Error ? res.message : 'Company could not be saved';
        console.error('Error creating company:', reason);
        this.loading = false;
        this.themeService.hideSpinner();

        this.serverMessage = `Error creating company: ${reason}. Please try again.`;
        return;
      }

      const companyModel = new Company(res);
      this.companyService.company = companyModel;

      // Record this user as the company's owner. Membership is server-owned
      // (#874): the API verifies ownerId, then writes the company_members
      // record and the user's currentCompanyId / companies / companyRoles,
      // promoting a first-time owner to admin. The browser may no longer
      // write those fields.
      let link;
      try {
        // A channel-partner code from the signup link (#943) goes with it; the
        // API checks it and stamps an active partner on the company.
        link = await firstValueFrom(
          this.membershipService.linkCompanyOwner(companyModel.uid, readPendingPartnerCode()),
        );
      } catch (linkError: any) {
        link = { success: false, error: linkError?.error?.error || linkError?.message };
      }
      if (!link?.success) {
        console.error('Failed to link company to user:', link?.error);
        this.serverMessage = `Company created but failed to link to your account: ${link?.error || 'unknown error'}. Please try again.`;
        this.loading = false;
        this.themeService.hideSpinner();
        return;
      }

      // The API has checked the partner code and cleared its own copy.
      clearPendingPartnerCode();

      const updatedUser: Partial<User> = {
        currentCompanyId: companyModel.uid,
        companies: Array.isArray(this.user.companies)
          ? Array.from(new Set([...(this.user.companies || []), companyModel.uid]))
          : [companyModel.uid],
        companyRoles: {
          ...(this.user.companyRoles || {}),
          [companyModel.uid]: 'owner',
        },
        ...(link.promotedToAdmin ? { role: 'admin' } : {}),
      };

      // Non-privileged signup fields the client still owns. Mark scanning
      // config as completed so the auth-flow guard doesn't redirect new users
      // to /register/scanning-config, and record the intended tier for
      // analytics. Every new registration is headed for a paid plan (#847).
      const profileUpdate: Partial<User> = {
        scanningConfigCompleted: true,
        intendedTier: 'premium',
        // The quick-signup draft name has served its purpose.
        pendingCompanyName: null,
      };
      const updateResult = await this.userService.update(profileUpdate, this.user.uid);
      if (updateResult instanceof Error) {
        // The company and its membership exist; only these flags are missing,
        // and the auth-flow guard recovers them. Not worth failing signup.
        console.warn('Failed to save signup profile flags:', updateResult);
      }
      Object.assign(updatedUser, profileUpdate);

      // Refresh local user model to reflect linkage immediately
      this.user = new User({ ...this.user, ...updatedUser });
      this.userService.setUserModel(this.user);

      // Also update localStorage to ensure persistence
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(this.user));
      }

      // Republish the session from Firestore before routing. The company
      // linkage above is written to the user document and to UserService, but
      // the wait page reads AuthService's copy — hydrated before the company
      // existed. Without this the dashboard loads with no company context
      // ("User has no company ID assigned"), and only signing out and back in
      // recovered it, because that is what clears the in-memory copy (#524).
      await this.authService.refreshHydratedUser();

      this.loading = false;
      this.themeService.hideSpinner();

      // No eval account or trial here: the company stays on no plan until
      // Stripe checkout or a sales-granted trial (#847).
      // All new registrations go directly to success; select-plan is no longer part of signup
      if (isPlatformBrowser(this.platformId) && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('registrationSuccessAllowed', '1');
      }
      await this.router.navigate(['/register/success'], { replaceUrl: true });
    } catch (error) {
      console.error('Form submission error:', error);
      this.serverMessage = 'Error processing form. Please try again.';
      this.loading = false;
      this.themeService.hideSpinner();
    }
  }

  randomString(length: number) {
    // Use cryptographically secure random for string generation with rejection sampling
    const chars =
      '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const charsLength = chars.length;

    let result = '';
    const maxValid = 256 - (256 % charsLength);

    for (let i = 0; i < length; i++) {
      let randomIndex;
      do {
        // Generate a new random value each iteration
        const randomValue = new Uint8Array(1);
        if (isPlatformBrowser(this.platformId)) {
          window.crypto.getRandomValues(randomValue);
        } else {
          randomValue[0] = Math.floor(Math.random() * 256);
        }
        randomIndex = randomValue[0];
      } while (randomIndex >= maxValid); // Rejection sampling to avoid modulo bias

      result += chars[randomIndex % charsLength];
    }
    return result;
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.companyForm.controls).forEach((field) => {
      const control = this.companyForm.get(field);
      control?.markAsTouched({ onlySelf: true });

      // Also mark nested form group controls as touched
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((nestedField) => {
          control.get(nestedField)?.markAsTouched({ onlySelf: true });
        });
      }
    });
  }

  /**
   * Check if a field is invalid and should show error
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.companyForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  /**
   * Get error message for a field
   */
  getFieldError(fieldName: string): string {
    const field = this.companyForm.get(fieldName);
    if (field?.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        const fieldDisplayNames: { [key: string]: string } = {
          name: 'Company name',
          industry: 'Industry',
          companySize: 'Company size',
          businessType: 'Business type',
          'billingAddress.street': 'Street address',
          'billingAddress.city': 'City',
          'billingAddress.state': 'State/Province',
          'billingAddress.postalCode': 'Postal code',
          'billingAddress.country': 'Country',
        };
        const displayName = fieldDisplayNames[fieldName] || fieldName;
        return `${displayName} is required`;
      }
      if (field.errors['minlength']) {
        return `Company name must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  /**
   * Get tier requirement explanation text
   */
  getTierRequirementText(): string {
    if (this.isTrialOrPremium) {
      return 'Complete business information is required to set up your account and billing.';
    }
    return 'Additional business information is optional.';
  }

  /**
   * Check if any tier-specific fields are required but incomplete
   */
  hasTierSpecificIncompleteFields(): boolean {
    if (!this.isTrialOrPremium) return false;

    const industry = this.companyForm.get('industry')?.value;
    const companySize = this.companyForm.get('companySize')?.value;
    const businessType = this.companyForm.get('businessType')?.value;
    const billingAddress = this.companyForm.get('billingAddress')?.value;

    return (
      !industry ||
      !companySize ||
      !businessType ||
      !billingAddress?.street ||
      !billingAddress?.city ||
      !billingAddress?.state ||
      !billingAddress?.postalCode ||
      !billingAddress?.country
    );
  }

  // async createMsspAccount(companyData: Company, user: User) {
  //   this.msspService.createMsspAccount(companyData, user);
  // }
}
